import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import api, { fmtMoney, fmtNum } from "../lib/api";
import {
    LayoutDashboard, FileText, ShoppingCart, TrendingUp, Truck,
    AlertTriangle, Receipt, PackageX, ArrowUpRight,
} from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    LineChart, Line, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { Link } from "react-router-dom";
import { Skeleton } from "../components/ui/skeleton";

function Kpi({ icon: Icon, label, value, sub, tone = "default", to, testid }) {
    const tones = {
        default: "text-primary bg-primary/10",
        warn: "text-amber-600 bg-amber-500/10",
        danger: "text-rose-600 bg-rose-500/10",
        success: "text-emerald-600 bg-emerald-500/10",
    };
    const Card = to ? Link : "div";
    return (
        <Card
            to={to}
            data-testid={testid}
            className="kpi-card block group cursor-pointer"
        >
            <div className="flex items-start justify-between">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center ${tones[tone]}`}>
                    <Icon size={18} />
                </div>
                {to && (
                    <ArrowUpRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
            </div>
            <div className="mt-4">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-medium">
                    {label}
                </div>
                <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
                {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
            </div>
        </Card>
    );
}

export default function Dashboard() {
    const [k, setK] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/dashboard/kpis").then((r) => {
            setK(r.data);
            setLoading(false);
        });
    }, []);

    return (
        <AppShell title="Kontrol Paneli" subtitle="Genel Bakış">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="kpi-grid">
                {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="kpi-card">
                            <Skeleton className="w-9 h-9 rounded-md" />
                            <Skeleton className="w-24 h-3 mt-4" />
                            <Skeleton className="w-16 h-6 mt-2" />
                        </div>
                    ))
                ) : (
                    <>
                        <Kpi icon={FileText} label="Bekleyen Talep" value={k.pending_prs} sub={`${k.pending_approvals} onay bekliyor`} to="/purchase-requests" testid="kpi-pending-prs" />
                        <Kpi icon={ShoppingCart} label="Açık Sipariş" value={k.open_pos} to="/purchase-orders" testid="kpi-open-pos" />
                        <Kpi icon={Truck} label="Geciken Teslimat" value={k.late_deliveries} tone={k.late_deliveries ? "warn" : "default"} testid="kpi-late" />
                        <Kpi icon={TrendingUp} label="Aylık Harcama" value={fmtMoney(k.monthly_spend, "TRY")} sub="Bu ay" tone="success" testid="kpi-spend" />
                        <Kpi icon={Receipt} label="Bekleyen Fatura" value={k.outstanding_invoices} to="/invoices" testid="kpi-outstanding-inv" />
                        <Kpi icon={AlertTriangle} label="Uyumsuz Fatura" value={k.mismatch_invoices} tone={k.mismatch_invoices ? "danger" : "default"} to="/invoices" testid="kpi-mismatch" />
                        <Kpi icon={PackageX} label="Düşük Stok" value={k.low_stock} tone={k.low_stock ? "warn" : "default"} to="/stock" testid="kpi-low-stock" />
                        <Kpi icon={LayoutDashboard} label="Kalem Sayısı" value={(k.top_suppliers?.length || 0) + " tedarikçi"} sub="Aktif tedarikçi" testid="kpi-suppliers" />
                    </>
                )}
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2 bg-card border border-border rounded-md p-5">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Trend</div>
                            <div className="text-base font-semibold mt-0.5">Aylık Satın Alma</div>
                        </div>
                        <div className="text-xs text-muted-foreground">Son 6 ay</div>
                    </div>
                    <div style={{ height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={k?.trend || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                                <Tooltip
                                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                                    formatter={(v) => fmtMoney(v)}
                                />
                                <Line type="monotone" dataKey="total" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-card border border-border rounded-md p-5">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Dağılım</div>
                    <div className="text-base font-semibold mt-0.5 mb-4">Projelere Göre Harcama</div>
                    {(k?.by_project || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground py-10 text-center">Henüz sipariş yok</div>
                    ) : (
                        <div style={{ height: 260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={k.by_project}
                                        dataKey="total"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        innerRadius={45}
                                        paddingAngle={2}
                                    >
                                        {(k?.by_project || []).map((_, i) => (
                                            <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(v) => fmtMoney(v)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-card border border-border rounded-md p-5">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Tedarikçi</div>
                        <div className="text-base font-semibold mt-0.5">En Yüksek Harcama</div>
                    </div>
                    <Link to="/suppliers" className="text-xs text-primary hover:underline">Tümü →</Link>
                </div>
                {(k?.top_suppliers || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground py-10 text-center">Henüz veri yok</div>
                ) : (
                    <div style={{ height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={k.top_suppliers} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                                <YAxis dataKey="name" type="category" width={200} tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} />
                                <Tooltip formatter={(v) => fmtMoney(v)} />
                                <Bar dataKey="total" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </AppShell>
    );
}
